import React, {ReactElement} from 'react'
import {Util} from 'yougile-util'
import {StickerColor, StickerIcon} from 'yougile-model'
import {IconStickerColor} from '@components/common/base/design-system'
import {Icon} from '@components/common/base/icon'
import {
  TwCustomIconWrapper,
  TwSticker,
  TwStickerIconBackground,
  TwStickerRequired,
  TwStickerTitle
} from '@components/common/base/sticker/containers'
import {CustomIcons} from '@components/common/icon-tabler/custom-icons-16'
import {TablerIcons} from '@components/common/icon-tabler/icon-tabler'
import {Tooltip} from '@components/common/tooltip/tooltip'

type DragEvent = React.DragEvent

export interface StickerProps {
  icon?: CustomIcons | StickerIcon | JSX.Element
  color?: StickerColor
  iconColor?: IconStickerColor
  iconBackgroundColor?: IconStickerColor
  alwaysShowIconBackground?: boolean
  rightIconColor?: IconStickerColor | 'action-default'
  isDraggable?: boolean
  isDragging?: boolean
  showDraggableStyle?: boolean
  active?: boolean
  title?: string | JSX.Element
  onClick?: (event: React.MouseEvent) => void
  onMouseDown?: () => void
  rightIcon?: CustomIcons | JSX.Element
  onDragStart?: (event: DragEvent) => void
  onDragEnd?: (event: DragEvent) => void
  stickerRef?: React.ForwardedRef<HTMLDivElement>
  className?: string
  textClassName?: string
  leftIconClassName?: string
  required?: boolean
  hasError?: boolean
  hint?: React.ReactNode
  disabled?: boolean
}

export const Sticker = React.forwardRef(
  ({hint, ...props}: StickerProps, ref) => {
    if (!hint) {
      return <StickerWithoutHint {...props} ref={ref} />
    }

    // Delay is used to prevent conflicts w/ tooltips inside the sticker item
    return (
      <Tooltip content={hint} placement="bottom" delay={50}>
        <StickerWithoutHint {...props} ref={ref} />
      </Tooltip>
    )
  }
)

const StickerWithoutHint = React.forwardRef(
  (props: Omit<StickerProps, 'hint'>, ref) => {
    const {
      color = 'sticker-white',
      active,
      isDragging,
      isDraggable,
      showDraggableStyle = true,
      icon,
      title,
      rightIcon,
      iconColor,
      iconBackgroundColor,
      alwaysShowIconBackground,
      rightIconColor,
      className,
      textClassName,
      leftIconClassName,
      required,
      hasError,
      disabled,
      ...rest
    } = props

    const onClickHandler = (event: React.MouseEvent) => {
      props.onClick?.(event)
    }

    return (
      <TwSticker
        data-test="dad-sticker"
        ref={ref}
        draggable={Boolean(isDraggable)}
        onClick={onClickHandler}
        onMouseDown={rest.onMouseDown}
        onDragStart={rest.onDragStart}
        onDragEnd={rest.onDragEnd}
        $color={color}
        $isDragging={Boolean(isDragging)}
        $isDraggable={Boolean(isDraggable)}
        $showDraggableStyle={showDraggableStyle}
        $isActive={Boolean(active)}
        $hasLeftIcon={Boolean(icon)}
        $hasRightIcon={Boolean(rightIcon)}
        $hasError={hasError}
        $isMac={isMac}
        $disabled={disabled}
        className={className}
      >
        <StickerIconWithBackground
          color={iconColor}
          backgroundColor={iconBackgroundColor}
          alwaysShowBackground={alwaysShowIconBackground}
          icon={icon}
          className={leftIconClassName}
        />
        <TwStickerTitle className={textClassName}>{title}</TwStickerTitle>
        {required && <TwStickerRequired>*</TwStickerRequired>}
        <StickerIconWithBackground color={rightIconColor} icon={rightIcon} />
      </TwSticker>
    )
  }
)

const StickerIconWithBackground = ({
  icon,
  color = 'action-default',
  backgroundColor,
  alwaysShowBackground,
  className
}: {
  icon?: TablerIcons | StickerIcon | JSX.Element
  color?: IconStickerColor | 'action-default'
  backgroundColor?: IconStickerColor
  alwaysShowBackground?: boolean
  className?: string
}) => {
  if (!icon) {
    return null
  }
  if (React.isValidElement(icon)) {
    return alwaysShowBackground ? (
      <WithBackground backgroundColor={backgroundColor}>
        <TwCustomIconWrapper $color={color} $size={16} className={className}>
          {icon}
        </TwCustomIconWrapper>
      </WithBackground>
    ) : (
      icon
    )
  }

  return (
    <WithBackground backgroundColor={backgroundColor}>
      <Icon color={color} icon={icon as TablerIcons} className={className} />
    </WithBackground>
  )
}

export const WithBackground = ({
  backgroundColor,
  children
}: {
  backgroundColor?: IconStickerColor
  children: ReactElement
}) => {
  if (!backgroundColor) {
    return <>{children}</>
  }

  return (
    <TwStickerIconBackground $color={backgroundColor}>
      {children}
    </TwStickerIconBackground>
  )
}

const isMac = Util.isMac()
